// 介錯「執行の咆哮」(executioner_phaseTransition) の3つの波を「ボスから外へ広がる空気の衝撃波」にする (2026-10-09 夜に開発版の比較版として作り、
//   2026-10-12 に既定 = 配布版へ: ユーザー採用・試遊パック 10-10 の答え B-1「見えるようになった分で、最後の突進斬りは弾きやすいか」=「足りる」)。
//
//   ユーザー (10-09・窓口経由): 3つの波は「空気を伝わる衝撃波」(吠えた声の衝撃がボスから外へ広がって来る・主人公に届く瞬間を弾く・床は関係ない)。
//     見せ方は「空気の圧の系統で」(戴冠獣の吠え src/hound_howl_fx.ts と同じ系統)。「音波の輪は消して良い」。
//     選んだ物 = 速さ 案B (420px/秒・間隔 600/560ms・溜め 2.3→約3.6秒) / 突進斬りは決まった時刻 / ガードで受けられる /
//     届く範囲 460px のまま (下がれば逃げられる)。1つ弾いたら場の広い方へ吹き飛び、残りの波は当たらない (?roarParry の roomy を流用)。
//   案と試算 = C:/dev/LUNAIRE-notes/assets/2026-10-09-roar-airwave/roar-airwave-options.md・文書 = docs/research/2026-10-09-executioner-roar-airwave-status.md
//
//   既定 = この空気の衝撃波 (真ん中へ移ってから吠える・飛ぶ向き away)。このファイルの読み込みの最後で setRoarAir(true, 'away', true) を当てる (URL は読まない)。
//   旧 (床の帯の波) は DEV の ?roarAir=0 で戻す。URL の読み取りは src/game.ts の import.meta.env.DEV の箱の中だけ。
//   この版の時は、床の帯の当たり・音波の輪・頭の光・?roarTell・?roarParry の帯は出さない (版どうしを重ねない。それらの比較版は ?roarAir=0 の時だけ意味を持つ)。
//
//   時間割 (溜めの頭 = 0ms):
//     0〜300 待機 → 吠えの構え / 〜1050 吸気 (胸が膨らむ) / 1050 波1 (「吠えた」の山と同時)
//     1450〜1650 胸が膨らむ合図 → 1650 波2 / 2010〜2210 胸が膨らむ合図 (いちばん大きい) → 2210 波3
//     3358 踏み切りの沈み → 3565 突進斬り (当たりは active の 0.45 = 3655)
//   波は 420px/秒でボスの中心から左右へ広がり、主人公の中心がボスから 460px 以内なら前縁が触れた一瞬に当たる。
//   弾ける (ジャスト/普通は白い攻撃と同じ)・ガードで受けられる (3つ目で崩れる)・回避の無敵で抜けられる。傷と体幹は今のまま。
import { getAttack } from './attacks'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { resetRoarTell } from './exec_roar_tell'
import { resetRoarWave, ROAR_KNOCK_DEFAULT_PX } from './exec_roar_wave'
import { setRoarCenter } from './exec_roar_center'
import { airWaveActiveMs } from './roar_air_geometry'
import type { AirWaveSpec, HazardSpawn } from './types'

const ATTACK_ID = 'executioner_phaseTransition'
const BOSS_ID = 'the-executioner'

export const ROAR_AIR = {
  speedPxPerSec: 420,
  reachPx: 460,
  /** 真ん中へ移ってから吠える版 (既定・src/exec_roar_center.ts) の届く範囲。10-10 ユーザー「届く範囲を400px前後に縮める」= 逃げ道を残す。
   *  真ん中 (480) から壁ぎわの主人公の中心までは 420px。390 なら壁に寄った 30px (主人公の体1つ分) が当たらない。
   *  真ん中へ移らない版 (&roarAirCenter=0) は 460 のまま。表 = docs/research/2026-10-10-executioner-roar-air-center.md §9 */
  centerReachPx: 390,
  thicknessPx: 24,
  /** 波の出る時刻 (溜めの頭から ms)。波1 = 「吠えた」の山 (render.ts roarReleaseIntensity の 1050) と同時。 */
  emitMs: [1050, 1650, 2210] as const,
  /** 波2・3の前の、胸が膨らむ合図の長さ (ms)。 */
  chestLeadMs: 200,
  /** 溜めの長さ。波3が届く範囲の端に着いてから 350ms 後に突進斬りが当たる (案B)。 */
  chargeMs: 3565,
  /** 踏み切りの沈み (kick_windup) を溜めの終わりの何 ms 前に出すか (今の 2300×0.09 ≒ 207 と同じ)。 */
  kickLeadMs: 207,
  /** 弾いた時に飛ぶ距離 (?roarParry と同じ既定)。 */
  knockPx: ROAR_KNOCK_DEFAULT_PX,
} as const

// 傷・体幹は今の床の波と同じ (10/15・10/15・15/20)。
const WAVE_HITS = [
  { damage: 10, postureDamage: 15, level: 1 },
  { damage: 10, postureDamage: 15, level: 2 },
  { damage: 15, postureDamage: 20, level: 3 },
] as const

// 10-30 作者 roar3「遅いから漂ってるように見える」→ roar4「新しい版を入れる」: 前縁の速さは 1100px/秒 (届く範囲の端まで約 0.4 秒) が配布版の既定。
//   当たりも同じ速さで動く (見た目と当たりをずらさない)。DEV ?execRoarWave=old で前の 420px/秒。調べた数値 = docs/research/2026-10-30-exec-roar-shockwave.md
//   既定はここで持つ (game.ts の DEV の囲いは配布版で走らないため・10-31 docs/research/2026-10-31-dev-gated-defaults.md)。
export const ROAR_AIR_RING_SPEED = 1100
let ringSpeed = true
export function setRoarAirRingSpeed(on: boolean): void { ringSpeed = on }
export function roarAirSpeedPxPerSec(): number { return ringSpeed ? ROAR_AIR_RING_SPEED : ROAR_AIR.speedPxPerSec }

export function roarAirSpec(level: 1 | 2 | 3, reachPx: number = ROAR_AIR.reachPx): AirWaveSpec {
  return { speedPxPerSec: roarAirSpeedPxPerSec(), reachPx, thicknessPx: ROAR_AIR.thicknessPx, level }
}

/** この版の hazardSpawns (波1つ = 当たり1つ。左右の前縁を1つの当たりで持つ)。dir = 弾いた時に飛ぶ向き。 */
export function roarAirSpawns(dir: RoarAirDir = 'roomy', reachPx: number = ROAR_AIR.reachPx): HazardSpawn[] {
  return WAVE_HITS.map((w, i) => {
    const spec = roarAirSpec(w.level, reachPx)
    return {
      anchor: 'boss', offset: 0, width: 0, kind: 'roarWave',
      telegraphMs: ROAR_AIR.emitMs[i]!, activeMs: airWaveActiveMs(spec),
      damage: w.damage, postureDamage: w.postureDamage, hitOnce: true,
      parryable: true, parryKnockbackPx: ROAR_AIR.knockPx, parryKnockbackToward: dir, parryEndsCast: true,
      airWave: spec,
    }
  })
}

// ===== 絵の時間割 (ms で置き直す。今の版は溜め 2300 の比率で書いてあるので、溜めを延ばすと全部ずれる) =====
const at = (ms: number): number => ms / ROAR_AIR.chargeMs
// 胸の膨らみ: 足元を支点に少し大きく (縦横)・頭を少し反らす。吐き出す瞬間に前へ押し出して戻る。波3がいちばん大きい。
type Swell = { sx: number; sy: number; rot: number }
const SWELL: Record<1 | 2 | 3, Swell> = {
  1: { sx: 1.02, sy: 1.025, rot: -1.5 },
  2: { sx: 1.025, sy: 1.03, rot: -1.5 },
  3: { sx: 1.04, sy: 1.045, rot: -2.2 },
}
function swellKeys(emit: number, leadMs: number, s: Swell): BossAttackTimeline {
  return [
    { phase: 'telegraph', at: at(emit - leadMs), image: 'roar_pose', hold: true, ease: 'easeIn' },
    { phase: 'telegraph', at: at(emit), image: 'roar_pose', hold: true, ease: 'easeOut',
      scale: { x: s.sx, y: s.sy }, rot: s.rot },
    // 吐き出し: 縦を戻して前へ倒す (声を押し出す)
    { phase: 'telegraph', at: at(emit + 70), image: 'roar_pose', hold: true, ease: 'easeInOut',
      scale: { x: 1 + (s.sx - 1) * 0.8, y: 1 - (s.sy - 1) * 0.2 }, rot: -s.rot * 0.6 },
    { phase: 'telegraph', at: at(emit + 260), image: 'roar_pose', hold: true },
  ]
}

export const ROAR_AIR_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
  { phase: 'telegraph', at: at(196), image: 'idle' },
  { phase: 'telegraph', at: at(300), image: 'roar_pose', hold: true, tag: 'anticipation' },
  // 吸気 (〜1050) の終わりの 250ms で胸が膨らむ → 1050 で吠える (波1)。
  ...swellKeys(ROAR_AIR.emitMs[0], 250, SWELL[1]),
  ...swellKeys(ROAR_AIR.emitMs[1], ROAR_AIR.chestLeadMs, SWELL[2]),
  ...swellKeys(ROAR_AIR.emitMs[2], ROAR_AIR.chestLeadMs, SWELL[3]),
  // 踏み切り: 突進の 207ms 前に沈む (今の版と同じ間)。
  { phase: 'telegraph', at: at(ROAR_AIR.chargeMs - ROAR_AIR.kickLeadMs), image: 'kick_windup', hold: true },
  { phase: 'active', at: 0.0, image: 'dash_travel', hold: true },
  { phase: 'active', at: 0.45, image: 'horizontal_slash', hold: true, tag: 'contact' },
  { phase: 'active', at: 0.8, image: 'slash_followthrough', hold: true },
  { phase: 'recovery', at: 0.0, image: 'slash_followthrough', hold: true },
  { phase: 'recovery', at: 0.55, image: 'idle', hold: true },
]

// ===== 切り替え =====
// 弾いた時に飛ぶ向き。roomy = 主人公から左右の壁までの余白が大きい方。away = ボスから離れる向き (波が押す向き)。
//   10-09 夜の撮影で、いつもの 300px・後ろの壁が近い所で弾くと、roomy は 300px 飛んでボスの真上に着いた → 比べる用に away を置いた。
// 10-10 (ユーザー「幕２にはいった時にボスが動画みたいに真ん中に移動すれば良さそう。弾けたら介錯から距離が取れて最後の１発が弾きやすくなる」):
//   既定 = 幕2の頭に真ん中へ移ってから吠える (src/exec_roar_center.ts)・飛ぶ向き = away。
//   真ん中に立つ介錯へ roomy (主人公から見た壁までの余白) を使うと、介錯の側がいつも広いと出て介錯を飛び越えるため。
//   介錯が真ん中なので、離れる向き (主人公のいる側の半分) にも場が残る = これがこの版の「場の広い方」。
//   比べる用 (DEV): &roarAirCenter=0 = 真ん中へ移らない (10-09 夜の版・既定の向き roomy) / &roarAirDir=roomy|away = 向きだけ上書き / ?roarAir=0 = 旧 (床の帯の波)。
export type RoarAirDir = 'roomy' | 'away'
let on = false
let dir: RoarAirDir = 'roomy'
let saved: { chargeMs: number; hazardSpawns: HazardSpawn[] | undefined; timeline: BossAttackTimeline | undefined } | null = null

export function roarAirOn(): boolean { return on }
export function roarAirDir(): RoarAirDir { return dir }

/** 空気の衝撃波にするか。?roarAir=0 だけが旧 (床の帯) = false。指定なし・1・他の値は既定の空気の衝撃波。 */
export function parseRoarAir(raw: string | null): boolean { return raw !== '0' }

// URL の値から切り替える (DEV の箱の中だけ)。roarAir=0 = 旧 (床の帯の波) へ戻す。それ以外 (指定なし・1 含む) は既定 = 空気の衝撃波。
//   既定 = 真ん中へ移る・向き away。&roarAirCenter=0 で真ん中へ移らない (向きの既定は roomy)。&roarAirDir=roomy|away で向きを上書き。
export function setRoarAirFromParams(raw: string | null, rawDir: string | null = null, rawCenter: string | null = null): void {
  if (!parseRoarAir(raw)) { setRoarAir(false); return }
  const center = rawCenter !== '0'
  const d: RoarAirDir = rawDir === 'away' || rawDir === 'roomy' ? rawDir : center ? 'away' : 'roomy'
  setRoarAir(true, d, center)
}

export function setRoarAir(next: boolean, nextDir: RoarAirDir = 'roomy', center = false): void {
  setRoarCenter(next && center)
  const atk = getAttack(ATTACK_ID)
  const table = BOSS_ATTACK_TIMELINES[BOSS_ID]!
  if (saved) {
    atk.chargeMs = saved.chargeMs
    atk.hazardSpawns = saved.hazardSpawns
    if (saved.timeline) table[ATTACK_ID] = saved.timeline
    saved = null
  }
  on = next
  dir = next ? nextDir : 'roomy'
  if (!next) return
  // 床の帯の比較版 (?roarParry・?roarTell) は外してから当てる (版どうしを重ねない)。
  resetRoarWave()
  resetRoarTell()
  saved = { chargeMs: atk.chargeMs, hazardSpawns: atk.hazardSpawns, timeline: table[ATTACK_ID] }
  atk.chargeMs = ROAR_AIR.chargeMs
  atk.hazardSpawns = roarAirSpawns(dir, center ? ROAR_AIR.centerReachPx : ROAR_AIR.reachPx)
  table[ATTACK_ID] = ROAR_AIR_TIMELINE
}

/** 旧 (床の帯の波) へ戻す (?roarAir=0 と同じ)。既定へ戻す時は setRoarAir(true, 'away', true)。 */
export function resetRoarAir(): void { setRoarAir(false) }

/** 咆哮の溜めの頭から何 ms で波 i (0..2) が出るか・その前の胸の合図の頭。描画 (render.ts の吠えの光) が読む。 */
export function roarAirEmitTimes(): readonly number[] { return ROAR_AIR.emitMs }

// 既定 (2026-10-12 ユーザー採用・配布版) = 空気の衝撃波・真ん中へ移ってから吠える・飛ぶ向き away。読み込みの最後に置く
//   (ROAR_AIR_TIMELINE・attacks の表・boss_pose_timeline の表が出来てから当てる。render.ts / game.ts がこのファイルを読むので、ゲームも自動プレイの測定も通る)。
setRoarAir(true, 'away', true)
